import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "../../lib/settings";

const links = [
  { to: "/", label: "Home" },
  { to: "/inventory", label: "Inventory" },
  { to: "/finance", label: "Finance" },
  { to: "/trade-in", label: "Trade-In" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const location = useLocation();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0F0F13]/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className="relative">
            <div className="w-10 h-10 rounded-full bg-[var(--primary)] flex items-center justify-center font-display text-xl">
              P
            </div>
            <div className="absolute inset-0 rounded-full bg-[var(--primary)] blur-lg opacity-40 group-hover:opacity-70 transition-opacity" />
          </div>
          <div className="leading-none">
            <div className="font-display text-2xl tracking-tight">{settings.business_name?.toUpperCase() || "PICK AUTO"}</div>
            <div className="overline text-[9px]">{settings.tagline}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`relative px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                  active ? "text-white" : "text-white/60 hover:text-white"
                }`}
              >
                {l.label}
                {active && (
                  <motion.span
                    layoutId="nav-underline"
                    className="absolute left-3 right-3 bottom-1 h-[2px] bg-[var(--primary)]"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <Link
          to="/inventory"
          data-testid="nav-cta"
          className="hidden md:inline-flex btn-primary text-sm"
        >
          Browse Cars
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden glass border-t border-white/10 px-6 py-6 flex flex-col gap-4"
          >
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className="text-white/80 hover:text-white text-lg"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/inventory" className="btn-primary self-start mt-2">Browse Cars</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
